/** Real client for the routing endpoints (api/app/routing/routes.py). Same origin + session cookie as the chat API. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type {
  RosterMonth,
  RosterWrite,
  RoutingApi,
  RoutingSettings,
  RoutingValues,
  ShiftWeek,
  ShiftWeekBody,
  TeamLoad,
} from './types'

export class HttpRoutingApi implements RoutingApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 15000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const url = new URL(`${this.base.replace(/\/+$/, '')}/api/v1${path}`, window.location.origin)
      const res = await this.fetchImpl(url.toString(), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.statusText || `HTTP ${res.status}`
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
          else if (Array.isArray(j.detail)) detail = 'ข้อมูลไม่ครบหรือไม่ถูกต้อง'
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  getSettings(): Promise<RoutingSettings> {
    return this.req('GET', '/settings/routing')
  }

  saveSettings(changes: Partial<RoutingValues>): Promise<RoutingSettings> {
    return this.req('PUT', '/settings/routing', changes)
  }

  getShifts(): Promise<ShiftWeek> {
    return this.req('GET', '/routing/shifts')
  }

  saveShifts(body: ShiftWeekBody): Promise<ShiftWeek> {
    return this.req('PUT', '/routing/shifts', body)
  }

  teamLoad(): Promise<TeamLoad> {
    return this.req('GET', '/team/load')
  }

  getRoster(month?: string): Promise<RosterMonth> {
    return this.req('GET', `/routing/roster${month ? `?month=${encodeURIComponent(month)}` : ''}`)
  }

  saveRoster(body: RosterWrite): Promise<RosterMonth> {
    return this.req('PUT', '/routing/roster', body)
  }

  setNickname(staffId: string, nickname: string | null): Promise<{ staff_id: string; nickname: string | null; changed: boolean }> {
    return this.req('PUT', '/routing/roster/nickname', { staff_id: staffId, nickname })
  }
}
